import React, { useRef, useState } from "react";

import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { MdPlayCircleOutline } from "react-icons/md";

import ImageCarouselModal, {
  ImageCarouselModalHandle,
  File,
} from "../../../../../../../components/ImageCarouselModal";
import { Recording } from "../../../../../../../models/recording";
import { VideoStatusRoomLog } from "../../../../../../../models/task.room.log";
import TaskService from "../../../../../../../services/task.service";
import { FileType, VideoStatus } from "../../../../../../../util/enums";
import useStyles, { VideoUploading } from "./styles";

interface Props {
  taskId?: number;
  videoHasBeenDeleted?: string;
}

const VideoTask = ({ taskId, videoHasBeenDeleted }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const videoCarouselModalRef = useRef<ImageCarouselModalHandle>(null);

  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loadVideoEnded, setLoadVideoEnded] = useState<boolean>(true);
  const [roomStatusLog, setRoomStatusLog] = useState<VideoStatusRoomLog>();

  const { isFetching } = useQuery(
    [`recording.list.${taskId}`, taskId],
    () => TaskService.getRecordings(Number(taskId)),
    {
      enabled: !!taskId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setRecordings(response.data);
      },
    }
  );

  const { isFetching: fetchingStatus } = useQuery(
    ["video.logs.list", taskId],
    () => TaskService.getVideoStatus(Number(taskId)),
    {
      enabled: !!taskId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setRoomStatusLog(response.data);
        }
      },
    }
  );

  const renderContent = () => {
    if (videoHasBeenDeleted === VideoStatus.DELETED) {
      return (
        <div className={`${classes.videoStyle} withoutVideo`}>
          <span className={classes.videoName}>{translate("recordDeleted")}</span>
        </div>
      );
    }

    if (isFetching || fetchingStatus) {
      return (
        <div className={`${classes.videoStyle} withoutVideo withAnimation`}>
          <MdPlayCircleOutline size={24} />
          <span className={classes.videoName}>{translate("fetchingVideo")}</span>
        </div>
      );
    }

    if (recordings?.length > 0) {
      return (
        <>
          {recordings.map((recording, index: number) => (
            <Grid item key={recording?.link || index} className={classes.grid}>
              <div className="position-relative w-100">
                {loadVideoEnded && (
                  <div className={classes.uploadProgress}>
                    <MdPlayCircleOutline size={24} />
                    <span className={classes.videoName}>{translate("videoUploadInProgress")}</span>
                  </div>
                )}
                <video
                  onLoadedMetadata={() => {
                    setTimeout(() => {
                      setLoadVideoEnded(false);
                    }, 650);
                  }}
                  src={recording.link || ""}
                  className={classes.videoStyle}
                  onClick={() => onOpenVideoModal(index)}
                />
                {!loadVideoEnded && (
                  <div className={classes.videoDescription}>
                    <MdPlayCircleOutline size={24} />
                    <span className={classes.videoName}>{translate("videoName")}</span>
                  </div>
                )}
              </div>
            </Grid>
          ))}
        </>
      );
    }

    if (roomStatusLog?.status?.videoRecorded && !videoHasBeenDeleted) {
      return (
        <VideoUploading>
          <span>{translate("videoUploadInProgress")}</span>
        </VideoUploading>
      );
    }

    return (
      <div className={`${classes.videoStyle} withoutVideo`}>
        <MdPlayCircleOutline size={24} />
        <span className={classes.videoName}>{translate("noVideoAvailable")}</span>
      </div>
    );
  };

  const onOpenVideoModal = (videoIdx: number) => {
    const videoFiles = recordings.map(
      (record) => ({ url: record.link, type: FileType.VIDEO } as File)
    );

    videoCarouselModalRef?.current?.handleOpenModal(videoIdx, videoFiles);
  };

  return (
    <div>
      <ImageCarouselModal ref={videoCarouselModalRef} />
      <span className={classes.titleVideo}>{translate("videos")}</span>
      <Grid container direction="column" alignItems="flex-start">
        {renderContent()}
      </Grid>
    </div>
  );
};

export default VideoTask;
